import React, { PureComponent } from 'react';
import onClickOutside from 'react-click-outside';
import { I18n } from 'react-redux-i18n';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { REDUCER_NAME as COMMON_REDUCER_NAME } from '../../../../app/commonReducer';
import ConstCommon from './../../../../common/Const';
import ScrollComponent from '../../../../common/ScrollComponent';
import {
  isNotLoginOrExpired,
  removeSpecialChar,
} from '../../../../helpers/Common';
import EventEmitter, {
  SHOW_GUEST_POPUP,
} from '../../../../helpers/EventEmitter';
import Layout from '../../../../helpers/Layout';
import { REDUCER_NAME } from '../notifi-maque/reducer';
import { components, quickComponents, tickerQuickComponents } from './Const';
import {
  componentLimit,
  lockComponents,
} from '../../../../../configs/WidgetConfig';
import PropTypes from 'prop-types';
import DropdownItem from './DropdownItem';

const ORGAN_NAME_MAX_LENGTH = 15;
const MAX_ITEM = 20;

class SearchPopup extends PureComponent {
  constructor(props) {
    super(props);
    this.componentRefs = {};
    this.dragSources = [];
    this.state = {
      activeIndex: -1,
    };
    this.scrollRef = React.createRef();
  }

  componentWillReceiveProps(nextProps, nextContext) {
    const { isShowList } = this.props;
    if (isShowList && !nextProps.isShowList) {
      this.scrollRef.current && this.scrollRef.current.scrollTop(0);
      this.setState({
        activeIndex: -1,
      });
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { currentCode, isShowList, keyword } = this.props;
    if (isShowList && !prevProps.isShowList) {
      window.addEventListener('keydown', this.onKeyDown);
    }

    if (!isShowList && prevProps.isShowList) {
      window.removeEventListener('keydown', this.onKeyDown);
    }

    if (prevProps.currentCode === null && currentCode) {
      this.createDragSource();
    }
    const isKeywordChange =
      keyword !== prevProps.keyword && keyword && prevProps.keyword;
    if (isKeywordChange) {
      this.removeDragSource();
      this.createDragSource();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKeyDown);
    this.removeDragSource();
  }

  getHeightPerItem = () => {
    const { fontSize } = this.props;
    const map = {
      Large: 31,
      Medium: 28,
      Small: 26,
    };

    return map[fontSize];
  };

  getPopupHeight = () => {
    return this.getHeightPerItem() * 10;
  };

  scrollIfNeeded = index => {
    this.scrollRef.current.scrollTop(index * this.getHeightPerItem());
  };

  onKeyDown = e => {
    const { isShowList, currentCode, addToHeadline, openWidget } = this.props;
    const { activeIndex } = this.state;

    if (e.key === 'ArrowDown') {
      const index = Math.min(activeIndex + 1, this.getSearchList().length);
      this.setState({
        activeIndex: index,
      });
      this.scrollIfNeeded(index);
      return;
    }

    if (e.key === 'ArrowUp') {
      const index = Math.max(activeIndex - 1, -1);
      this.setState({
        activeIndex: index,
      });
      this.scrollIfNeeded(index);
      return;
    }

    if (e.key === 'Enter' && isShowList && activeIndex !== -1) {
      if (activeIndex === 0) {
        addToHeadline(currentCode);
        return;
      }

      openWidget(this.getSearchList()[activeIndex - 1]);
    }
  };

  createDragSource = () => {
    this.getSearchList().forEach(component => {
      const componentName =
        typeof component === 'object' ? component.component : component;
      const refKey =
        typeof component === 'object' ? component.componentNameKey : component;
      if (this.isComponentLocked(component) || this.isDisabled(component)) {
        return;
      }
      const dragSource = Layout.drag(
        this.componentRefs[refKey],
        {
          title: componentName,
          name: componentName,
          tooltip: componentName,
        },
        { dragCode: this.getCode(), ...(component.props || {}) },
      );
      this.dragSources.push(dragSource);
    });
  };

  removeDragSource = () => {
    this.dragSources.forEach(source => {
      source._dragListener.destroy();
    });
    this.dragSources = [];
  };

  getCode = () => {
    const { currentCode } = this.props;
    return currentCode;
  };

  handleClickOutside = e => {
    const { hideList, inputRef } = this.props;
    if (e.target === inputRef.current) {
      return;
    }
    hideList();
  };

  getOrganName = () => {
    const { currentCode } = this.props;
    let name = currentCode.codeName;
    if (name.length > ORGAN_NAME_MAX_LENGTH) {
      name = `${name.substr(0, ORGAN_NAME_MAX_LENGTH)}...`;
    }

    return name;
  };

  replaceWithHighlight = (str, keyword) => {
    if (!keyword) {
      return str;
    }
    const strIndex = removeSpecialChar(str.toLowerCase()).indexOf(
      removeSpecialChar(keyword.toLowerCase()),
    );
    if (strIndex === -1) {
      return str;
    }
    const originalKeyword = str.substring(strIndex, strIndex + keyword.length);

    return (
      str.substring(0, strIndex) +
      `<span class="search-text-highlight">${originalKeyword}</span>` +
      str.substring(strIndex + keyword.length)
    );
  };

  getWidgetMiddleText = component => {
    const { keyword } = this.props;

    if (typeof component === 'object') {
      return this.replaceWithHighlight(component.componentDescription, keyword);
    }

    const text = `${this.getOrganName()} / ${I18n.t(
      `layout.quickAccess.${component}`,
    )}`;

    return this.replaceWithHighlight(text, keyword);
  };

  getHeadlineMiddleText = () => {
    const { currentCode } = this.props;
    return `${this.getOrganName()} / ${currentCode.comGroupCode}`;
  };

  isDisabled = component => {
    const { countListComponent } = this.props;
    const componentName =
      typeof component === 'object' ? component.component : component;

    return (
      countListComponent[componentName] === 1 &&
      componentLimit[componentName] === 1
    );
  };

  canNotAddHeadlineCode = () => {
    const { headlineCodes, currentCode } = this.props;

    return headlineCodes.some(item => item.code === currentCode.displayCode);
  };

  getAddHeadlineCodeClassName = () => {
    const { activeIndex } = this.state;
    let className = 'dropdown-item d-flex justify-content-between';
    if (activeIndex === 0) {
      className += ' active';
    }
    if (this.canNotAddHeadlineCode()) {
      className += ' disabled';
    }

    return className;
  };

  getComponents = () => {
    const { keyword } = this.props;
    const componentsWithName = components.map(component => {
      return {
        componentName: I18n.t(`common.listComponent.${component.component}`),
        componentDescription: I18n.t(
          `common.listComponentWithTab.${component.componentNameKey}`,
        ),
        ...component,
      };
    });

    const pattern = new RegExp(`${removeSpecialChar(keyword)}`, 'i');
    return componentsWithName
      .filter(component =>
        pattern.test(removeSpecialChar(component.componentDescription)),
      )
      .slice(0, MAX_ITEM);
  };

  getSearchList = () => {
    const { currentCode } = this.props;
    if (!currentCode) {
      return this.getComponents();
    }
    if (currentCode.isTicker) {
      return tickerQuickComponents.concat(this.getComponents());
    }
    if (currentCode.isDerivatives) {
      return quickComponents
        .filter(comp => comp !== ConstCommon.listComponent.PriceData)
        .concat(this.getComponents());
    }

    return quickComponents.concat(this.getComponents());
  };

  isShowAddToHeadline = () => {
    const { currentCode } = this.props;

    return currentCode && currentCode.isTicker;
  };

  isLockedProps = component => {
    if (typeof component === 'string') {
      return false;
    }

    if (
      [
        ConstCommon.listComponent.TopVolume,
        ConstCommon.listComponent.TopValue,
        ConstCommon.listComponent.TopGainers,
        ConstCommon.listComponent.TopLosers,
      ].includes(component.component)
    ) {
      return component.props.tab === 5;
    }

    if (ConstCommon.listComponent.MoneyFlow === component.component) {
      return component.props.tab === 3;
    }

    if (ConstCommon.listComponent.PriceData === component.component) {
      return component.props.tab === 4;
    }

    if (ConstCommon.listComponent.TASignals === component.component) {
      return component.props.tab === 2 || component.props.tab === 3;
    }

    if (ConstCommon.listComponent.News === component.component) {
      return component.props.tab !== 1;
    }

    return false;
  };

  isComponentLocked = component => {
    const { user } = this.props;
    const componentName =
      typeof component === 'object' ? component.component : component;

    return (
      (lockComponents[componentName] || this.isLockedProps(component)) &&
      isNotLoginOrExpired(user)
    );
  };

  onItemClick = component => {
    const { openWidget, hideList } = this.props;
    if (this.isDisabled(component)) {
      return;
    }
    if (this.isComponentLocked(component)) {
      hideList();
      EventEmitter.dispatch(SHOW_GUEST_POPUP);
      return;
    }

    openWidget(component);
  };

  render() {
    const { activeIndex } = this.state;
    const searchList = this.getSearchList();
    const { currentCode, addToHeadline } = this.props;
    if (!searchList.length) {
      return (
        <div className="drop-search drop-mecus overflow-hidden drop-10">
          <div className="dropdown-item">{I18n.t('layout.noMatchedStock')}</div>
        </div>
      );
    }

    return (
      <div className="drop-search drop-mecus">
        <ScrollComponent
          scrollRef={this.scrollRef}
          appendStyle={{ maxHeight: this.getPopupHeight() }}
        >
          {this.isShowAddToHeadline() && (
            <DropdownItem
              onClick={() => addToHeadline(currentCode)}
              ticker={currentCode.displayCode}
              className={this.getAddHeadlineCodeClassName()}
              middleName={this.getHeadlineMiddleText()}
            >
              <div className="d-flex align-items-center add-to-headline">
                <span>{I18n.t('layout.addToHeadLine')}</span>
              </div>
            </DropdownItem>
          )}

          {searchList.map((component, index) => {
            const isLocked = this.isComponentLocked(component);
            const isDisabled = this.isDisabled(component);
            const className = ['dropdown-item d-flex justify-content-between'];
            if (index + 1 === activeIndex) {
              className.push('active');
            }
            if (isDisabled || isLocked) {
              className.push('disabled');
            }

            if (typeof component === 'object') {
              //Is component obj
              return (
                <DropdownItem
                  isLocked={isLocked}
                  isLarge={currentCode && !currentCode.isTicker}
                  showTicker={false}
                  componentRefs={this.componentRefs}
                  component={component.componentNameKey}
                  onClick={() => this.onItemClick(component)}
                  key={component.componentNameKey}
                  ticker={currentCode ? currentCode.displayCode : ''}
                  className={className.join(' ')}
                  middleName={this.getWidgetMiddleText(component)}
                >
                  <div className="d-flex align-items-center open-widget">
                    <span>{I18n.t('layout.openWidget')}</span>
                    <i className="icon-external ml-5" />
                  </div>
                </DropdownItem>
              );
            }

            return (
              <DropdownItem
                isLocked={isLocked}
                isLarge={!currentCode.isTicker}
                componentRefs={this.componentRefs}
                component={component}
                onClick={() => this.onItemClick(component)}
                key={component}
                ticker={currentCode.displayCode}
                className={className.join(' ')}
                middleName={this.getWidgetMiddleText(component)}
              >
                <div className="d-flex align-items-center open-widget">
                  <span>{I18n.t('layout.openWidget')}</span>
                  <i className="icon-external ml-5" />
                </div>
              </DropdownItem>
            );
          })}
        </ScrollComponent>
      </div>
    );
  }
}

SearchPopup.propTypes = {
  hideList: PropTypes.func.isRequired,
  currentCode: PropTypes.object,
  addToHeadline: PropTypes.func.isRequired,
  openWidget: PropTypes.func.isRequired,
  countListComponent: PropTypes.object.isRequired,
  headlineCodes: PropTypes.array.isRequired,
};

SearchPopup.defaultProps = {
  currentCode: null,
};

const mapStateToProps = state => ({
  countListComponent: state[COMMON_REDUCER_NAME].countListComponent,
  headlineCodes: state[REDUCER_NAME].headlineCodes,
  user: state.userInfo,
  i18n: state.i18n,
  fontSize: state.fontSize,
});

export default compose(
  connect(mapStateToProps),
  onClickOutside,
)(SearchPopup);
